import NoProgramsIcon from "@assets/icons/no-programs.svg";
import { useI18n } from "@core/i18n/i18n";
import { PatientBase } from "@domain/patient/patient";
import { AnalyticsCreateProgramLocation, sendAnalyticsEventCreateProgram } from "@domain/program/program-analytics";
import styled from "@emotion/styled";
import { MessageContainer } from "@ui/components/common/message-container";
import {
	CreateProgramButton,
	CreateProgramButtonType,
} from "@ui/pages/patient-details/components/create-program-button";
import { physioMeMobileBreakpoint, stack } from "@ui/style";
import React, { useCallback } from "react";
import ProgramScheduledIcon from "@assets/icons/program-scheduled.svg";
import { colorWithAlpha } from "@utils/color-utils";
import { colors } from "@ui/style/colors";

interface EmptyProgramsPlaceholderProps {
	patient: PatientBase;
	style?: React.CSSProperties;
	analyticsLocation: AnalyticsCreateProgramLocation;
	hasFutureProgram?: boolean;
}

export const EmptyProgramsPlaceholder: React.FC<EmptyProgramsPlaceholderProps> = ({
	patient,
	style,
	analyticsLocation,
	hasFutureProgram,
}) => {
	const { format } = useI18n();

	const sendCreateProgramEvent = useCallback(
		(fromTemplate: boolean) => sendAnalyticsEventCreateProgram(analyticsLocation, patient.analyticsId, fromTemplate),
		[analyticsLocation, patient.analyticsId],
	);

	return (
		<EmptyContainer style={style}>
			{hasFutureProgram ? (
				<MessageContainer
					iconSvg={ProgramScheduledIcon}
					title={format("patientDetails.progress.noOngoing.title")}
					description={format("patientDetails.progress.empty.description")}
				/>
			) : (
				<>
					<MessageContainer iconSvg={NoProgramsIcon} title={format("patientDetails.progress.emptyProgram.title")} />
					<CreateProgramButton
						patient={patient}
						buttonType={CreateProgramButtonType.New}
						onClick={sendCreateProgramEvent}
					/>
					<CreateProgramButton
						patient={patient}
						buttonType={CreateProgramButtonType.FromTemplate}
						onClick={sendCreateProgramEvent}
					/>
				</>
			)}
		</EmptyContainer>
	);
};

const EmptyContainer = styled.div`
	inset: 0;
	padding-top: 150px;
	${stack(16, "flexstart", "center")};
	z-index: 50;
	margin-bottom: 0 !important;
	background-color: ${colorWithAlpha(colors.white, 0.6)};

	@media ${physioMeMobileBreakpoint} {
		padding: 50px 0;
	}
`;
